package com.islamicmedia.shared.util

/**
 * Referral-code parsing for the apps: the client's copy of the server's `ReferralCodes`
 * (server/.../util/ReferralCodes.kt). The server doesn't depend on :shared, so the two are kept in
 * step by hand, and ReferralCodeFormatTest repeats the server test's normalisation cases so they
 * can't drift. A code that normalises differently here is a friend told "that code doesn't exist"
 * for a code that does. Codes are only ever minted by the server; there is no generate() here.
 *
 * A code is 8 characters of Crockford base32 (digits plus A-Z without I, L, O, U). Stored and sent
 * as "ABCD2345", shown to people as "ABCD-2345".
 */
object ReferralCodeFormat {

    /** Crockford's base32 alphabet, in value order. */
    const val ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"
    const val LENGTH = 8

    /** Dash-like characters a phone keyboard may substitute for "-" (smart punctuation):
     *  hyphen, non-breaking hyphen, figure dash, en dash, em dash, horizontal bar, minus sign. */
    private val DASHES = setOf('-', '\u2010', '\u2011', '\u2012', '\u2013', '\u2014', '\u2015', '\u2212')

    /**
     * The code inside a pasted share link: /r/CODE or ?refcode=CODE.
     *
     * The server writes this as `(?i)(?:/r/|[?&]refcode=)([^/?#&\s]+)`. Two changes keep it
     * identical on every platform: IGNORE_CASE instead of the inline `(?i)`, which JavaScript's
     * RegExp rejects; and Java's `\s` spelled out, because on JavaScript `\s` also matches Unicode
     * spaces and would cut a code short where the server would not.
     */
    private val LINK_CODE = Regex("""(?:/r/|[?&]refcode=)([^/?#& \t\n\u000B\f\r]+)""", RegexOption.IGNORE_CASE)

    /**
     * The longest input [normalize] looks at: room for the whole share message
     * ([com.islamicmedia.shared.presentation.ReferralCopy.shareText], a little over 200 characters
     * with the link) pasted into the code box, and plenty to spare. The server's ReferralCodes has
     * the same number.
     */
    const val MAX_INPUT = 1000

    /** True for a code already in canonical form (what the server mints and stores). */
    fun isCanonical(code: String?): Boolean =
        code != null && code.length == LENGTH && code.all { it in ALPHABET }

    /**
     * The canonical code in whatever a person typed or pasted, or null when it can't be one.
     * Forgiving on purpose, because people type these on phones: case, spaces, the dash (and its
     * look-alikes), invisible keyboard marks and Crockford's look-alikes (O for 0, I or L for 1)
     * are all fine, and so is pasting the whole share link or share message. Never throws.
     */
    fun normalize(raw: String?): String? {
        if (raw == null) return null
        var s = raw.sanitizeTypedInput()
        if (s.isEmpty() || s.length > MAX_INPUT) return null
        LINK_CODE.find(s)?.let { s = it.groupValues[1] }
        val code = buildString {
            for (ch in s) {
                if (ch.isWhitespace() || ch in DASHES) continue
                append(
                    when (val up = ch.uppercaseChar()) {
                        'O' -> '0'
                        'I', 'L' -> '1'
                        else -> up
                    }
                )
            }
        }
        return code.takeIf { isCanonical(it) }
    }

    /** "ABCD2345" -> "ABCD-2345". Anything not canonical is returned unchanged. */
    fun display(code: String): String =
        if (isCanonical(code)) code.substring(0, 4) + "-" + code.substring(4) else code
}
